import { apiClient } from './client';

// ── Types ─────────────────────────────────────────────────────

export interface GxZone {
  id: string;
  eventId: string;
  name: string;
  description: string | null;
  color: string;
  sortOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export type FeedItemType = 'Note' | 'EmergencyAlert' | 'StandardAction';
export type EmergencyType = 'Medical' | 'Security' | 'Bottleneck' | 'Fire' | 'Other';
export type FeedItemSeverity = 'Low' | 'Medium' | 'High' | 'Critical';

export interface GxFeedItem {
  id: string;
  eventId: string;
  zoneId: string;
  reporterId: string;
  type: FeedItemType;
  severity: FeedItemSeverity;
  message: string;
  emergencyType: EmergencyType | null;
  actionId: string | null;
  acknowledged: boolean;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
}

export interface GxStandardAction {
  id: string;
  eventId: string;
  label: string;
  icon: string;
  severity: FeedItemSeverity;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface GxSubscription {
  id: string;
  eventId: string;
  userId: string;
  zoneFilters: string[];
  subscribedAt: string;
}

export interface GxZoneAssignment {
  id: string;
  zoneId: string;
  userId: string;
  assignedAt: string;
}

export interface PaginatedFeedResponse {
  items: GxFeedItem[];
  nextCursor: string | null;
  count: number;
}

// ── DTO Types ─────────────────────────────────────────────────

export interface CreateZoneData {
  name: string;
  color: string;
  description?: string;
  sortOrder?: number;
}

export interface UpdateZoneData {
  name?: string;
  color?: string;
  description?: string;
  sortOrder?: number;
  active?: boolean;
}

export interface AssignMemberData {
  userId: string;
}

export interface CreateNoteData {
  zoneId: string;
  message: string;
}

export interface CreateEmergencyAlertData {
  zoneId: string;
  emergencyType: EmergencyType;
  message: string;
}

export interface TriggerActionData {
  actionId: string;
  zoneId: string;
  message?: string;
}

export interface CreateStandardActionData {
  label: string;
  icon: string;
  severity?: FeedItemSeverity;
  sortOrder?: number;
}

export interface UpdateStandardActionData {
  label?: string;
  icon?: string;
  severity?: FeedItemSeverity;
  sortOrder?: number;
  active?: boolean;
}

export interface FeedQueryParams {
  cursor?: string;
  limit?: number;
  zoneIds?: string[];
}

// ── Events ─────────────────────────────────────────────────────

export function getEvents(): Promise<{ id: string; name: string }[]> {
  return apiClient<{ id: string; name: string }[]>('/events');
}

// ── Zone CRUD ─────────────────────────────────────────────────

export function getZones(eventId: string): Promise<GxZone[]> {
  return apiClient<GxZone[]>(`/gx/${eventId}/zones`);
}

export function createZone(eventId: string, data: CreateZoneData): Promise<GxZone> {
  return apiClient<GxZone>(`/gx/${eventId}/zones`, {
    method: 'POST',
    body: data,
  });
}

export function updateZone(eventId: string, zoneId: string, data: UpdateZoneData): Promise<GxZone> {
  return apiClient<GxZone>(`/gx/${eventId}/zones/${zoneId}`, {
    method: 'PATCH',
    body: data,
  });
}

export function deleteZone(eventId: string, zoneId: string): Promise<void> {
  return apiClient<void>(`/gx/${eventId}/zones/${zoneId}`, {
    method: 'DELETE',
  });
}

// ── Zone Assignment ───────────────────────────────────────────

export function assignMember(
  eventId: string,
  zoneId: string,
  data: AssignMemberData,
): Promise<GxZoneAssignment> {
  return apiClient<GxZoneAssignment>(`/gx/${eventId}/zones/${zoneId}/assign`, {
    method: 'POST',
    body: data,
  });
}

export function unassignMember(
  eventId: string,
  zoneId: string,
  memberId: string,
): Promise<void> {
  return apiClient<void>(`/gx/${eventId}/zones/${zoneId}/unassign/${memberId}`, {
    method: 'DELETE',
  });
}

export function getZoneMembers(
  eventId: string,
  zoneId: string,
): Promise<GxZoneAssignment[]> {
  return apiClient<GxZoneAssignment[]>(`/gx/${eventId}/zones/${zoneId}/members`);
}

// ── Feed ──────────────────────────────────────────────────────

export function getFeed(eventId: string, params?: FeedQueryParams): Promise<PaginatedFeedResponse> {
  const searchParams = new URLSearchParams();
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.zoneIds && params.zoneIds.length > 0) {
    searchParams.set('zoneIds', params.zoneIds.join(','));
  }
  const qs = searchParams.toString();
  return apiClient<PaginatedFeedResponse>(`/gx/${eventId}/feed${qs ? `?${qs}` : ''}`);
}

export function getFeedUpdates(eventId: string, since: string): Promise<GxFeedItem[]> {
  return apiClient<GxFeedItem[]>(`/gx/${eventId}/feed/updates?since=${encodeURIComponent(since)}`);
}

// ── Feed Item Creation ────────────────────────────────────────

export function createNote(eventId: string, data: CreateNoteData): Promise<GxFeedItem> {
  return apiClient<GxFeedItem>(`/gx/${eventId}/notes`, {
    method: 'POST',
    body: data,
  });
}

export function createEmergencyAlert(
  eventId: string,
  data: CreateEmergencyAlertData,
): Promise<GxFeedItem> {
  return apiClient<GxFeedItem>(`/gx/${eventId}/emergency-alerts`, {
    method: 'POST',
    body: data,
  });
}

export function triggerAction(eventId: string, data: TriggerActionData): Promise<GxFeedItem> {
  return apiClient<GxFeedItem>(`/gx/${eventId}/actions/trigger`, {
    method: 'POST',
    body: data,
  });
}

export function acknowledgeFeedItem(eventId: string, itemId: string): Promise<GxFeedItem> {
  return apiClient<GxFeedItem>(`/gx/${eventId}/feed/${itemId}/acknowledge`, {
    method: 'PATCH',
  });
}

// ── Standard Actions ──────────────────────────────────────────

export function getStandardActions(eventId: string): Promise<GxStandardAction[]> {
  return apiClient<GxStandardAction[]>(`/gx/${eventId}/actions`);
}

export function createStandardAction(
  eventId: string,
  data: CreateStandardActionData,
): Promise<GxStandardAction> {
  return apiClient<GxStandardAction>(`/gx/${eventId}/actions`, {
    method: 'POST',
    body: data,
  });
}

export function updateStandardAction(
  eventId: string,
  actionId: string,
  data: UpdateStandardActionData,
): Promise<GxStandardAction> {
  return apiClient<GxStandardAction>(`/gx/${eventId}/actions/${actionId}`, {
    method: 'PATCH',
    body: data,
  });
}

export function deleteStandardAction(eventId: string, actionId: string): Promise<void> {
  return apiClient<void>(`/gx/${eventId}/actions/${actionId}`, {
    method: 'DELETE',
  });
}

// ── GX Config ─────────────────────────────────────────────────

export interface GxConfig {
  feedName: string;
  muted: boolean;
}

export function getGxConfig(): Promise<GxConfig> {
  return apiClient<GxConfig>('/gx/config');
}

export function updateGxConfig(data: { feedName?: string; muted?: boolean }): Promise<GxConfig> {
  return apiClient<GxConfig>('/gx/config', {
    method: 'PATCH',
    body: data,
  });
}

// ── Subscriptions ─────────────────────────────────────────────

export function subscribe(
  eventId: string,
  zoneFilters?: string[],
): Promise<GxSubscription> {
  return apiClient<GxSubscription>(`/gx/${eventId}/subscribe`, {
    method: 'POST',
    body: zoneFilters ? { zoneFilters } : undefined,
  });
}

export function unsubscribe(eventId: string): Promise<void> {
  return apiClient<void>(`/gx/${eventId}/unsubscribe`, {
    method: 'DELETE',
  });
}

export function getSubscription(eventId: string): Promise<GxSubscription> {
  return apiClient<GxSubscription>(`/gx/${eventId}/subscription`);
}
